import { useCallback, useReducer, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  continueConversation,
  deleteConversation,
  deleteMyNote,
  deleteNotebookNote,
  getAdminConversation,
  getAdminConversations,
  getTrackerCalls,
  getConversation,
  getConversations,
  getNotebookNote,
  getMyNote,
  getMyNotes,
  getMiloMe,
  getMiloNotebook,
  getMiloSettings,
  getMiloUsage,
  saveMiloSettings,
  sendChat,
  testMiloKey,
  wipeNotebook,
  wipeMyNotes,
  type AdminConversationFilter,
  type ChatRefusal,
  type MiloSettingsInput,
  type NotebookFolder,
  type TrackerCallFilter,
} from './api';
import { chatReducer, emptyChat, messagesOf, miloLanguage } from './chat';
import { readMiloStream } from './stream';

export const miloKeys = {
  all: ['fleet', 'milo'] as const,
  me: () => [...miloKeys.all, 'me'] as const,
  conversations: () => [...miloKeys.all, 'conversations'] as const,
  settings: () => [...miloKeys.all, 'settings'] as const,
  usage: (month: string | undefined) => [...miloKeys.all, 'usage', month ?? 'current'] as const,
  notebook: () => [...miloKeys.all, 'notebook'] as const,
  notebookNote: (folder: NotebookFolder, path: string | null, userId: string | undefined) => [...miloKeys.all, 'notebook', 'note', folder, userId ?? null, path] as const,
  myNotes: () => [...miloKeys.all, 'my-notes'] as const,
  myNote: (path: string | null) => [...miloKeys.all, 'my-notes', 'note', path] as const,
  adminConversations: (filter: AdminConversationFilter) => [...miloKeys.all, 'admin', 'conversations', filter] as const,
  trackerCalls: (filter: TrackerCallFilter) => [...miloKeys.all, 'admin', 'tracker-calls', filter] as const,
  adminConversation: (id: string) => [...miloKeys.all, 'admin', 'conversation', id] as const,
};

/** Whether the panel can ask; checked again every few minutes and after every answer */
export const useMiloMe = () => useQuery({ queryKey: miloKeys.me(), queryFn: getMiloMe, refetchInterval: 300_000, retry: false });
export const useConversations = (enabled: boolean) => useQuery({ queryKey: miloKeys.conversations(), queryFn: getConversations, enabled });
export const useMiloSettings = () => useQuery({ queryKey: miloKeys.settings(), queryFn: getMiloSettings });
export const useMiloUsage = (month: string | undefined) => useQuery({ queryKey: miloKeys.usage(month), queryFn: () => getMiloUsage(month) });

export function useDeleteConversation() {
  const queryClient = useQueryClient();
  return useMutation({ mutationFn: deleteConversation, onSettled: () => queryClient.invalidateQueries({ queryKey: miloKeys.conversations() }) });
}

export function useSaveMiloSettings() {
  const queryClient = useQueryClient();
  return useMutation({ mutationFn: (input: MiloSettingsInput) => saveMiloSettings(input), onSettled: () => queryClient.invalidateQueries({ queryKey: miloKeys.all }) });
}

export const useTestMiloKey = () => useMutation({ mutationFn: testMiloKey });

/** Damis AI's notebooks, for admins: the guide, the company's and every user's own */
export const useMiloNotebook = () => useQuery({ queryKey: miloKeys.notebook(), queryFn: getMiloNotebook });
export const useNotebookNote = (target: { folder: NotebookFolder; path: string; userId?: string } | null) =>
  useQuery({
    queryKey: miloKeys.notebookNote(target?.folder ?? 'company', target?.path ?? null, target?.userId),
    queryFn: () => getNotebookNote(target!.folder, target!.path, target!.userId),
    enabled: target !== null,
  });

export function useDeleteNotebookNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ folder, path, userId }: { folder: 'company' | 'me'; path: string; userId?: string }) => deleteNotebookNote(folder, path, userId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: miloKeys.notebook() }),
  });
}

/** Damis AI's notes about the signed-in user, for that user */
export const useMyNotes = (enabled: boolean) => useQuery({ queryKey: miloKeys.myNotes(), queryFn: getMyNotes, enabled });
export const useMyNote = (path: string | null) => useQuery({ queryKey: miloKeys.myNote(path), queryFn: () => getMyNote(path!), enabled: path !== null });

export function useDeleteMyNote() {
  const queryClient = useQueryClient();
  return useMutation({ mutationFn: deleteMyNote, onSettled: () => queryClient.invalidateQueries({ queryKey: miloKeys.myNotes() }) });
}

export function useWipeMyNotes() {
  const queryClient = useQueryClient();
  return useMutation({ mutationFn: wipeMyNotes, onSettled: () => queryClient.invalidateQueries({ queryKey: miloKeys.myNotes() }) });
}

/** The tracker's calls to Claude for admins, a page at a time; checked again every half minute */
export const useTrackerCalls = (filter: TrackerCallFilter) =>
  useInfiniteQuery({
    queryKey: miloKeys.trackerCalls(filter),
    queryFn: ({ pageParam }) => getTrackerCalls(filter, pageParam),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 30_000,
  });

/** Everyone's conversations for admins, a page at a time; checked again every half minute to show what is happening */
export const useAdminConversations = (filter: AdminConversationFilter) =>
  useInfiniteQuery({
    queryKey: miloKeys.adminConversations(filter),
    queryFn: ({ pageParam }) => getAdminConversations(filter, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 30_000,
  });

export const useAdminConversation = (id: string | undefined) =>
  useQuery({ queryKey: miloKeys.adminConversation(id ?? ''), queryFn: () => getAdminConversation(id!), enabled: id !== undefined, retry: false, refetchInterval: 30_000 });

export function useWipeNotebook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ folder, userId }: { folder: 'company' | 'me'; userId?: string }) => wipeNotebook(folder, userId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: miloKeys.notebook() }),
  });
}

/** One conversation with milo: ask, stop, start over, or reopen a stored one */
export function useMiloChat() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(chatReducer, emptyChat);
  const [refusal, setRefusal] = useState<ChatRefusal | null>(null);
  const running = useRef<AbortController | null>(null);

  /** false when the question was refused before any answer (the caller puts it back into the input) */
  const ask = useCallback(
    async (text: string): Promise<boolean> => {
      if (running.current) return false;
      const controller = new AbortController();
      running.current = controller;
      setRefusal(null);
      dispatch({ type: 'ask', id: crypto.randomUUID(), text });
      try {
        const sent = await sendChat(
          { conversationId: state.conversationId, text, context: { path: location.pathname, language: miloLanguage(i18n.language), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone } },
          controller.signal
        );
        if (!sent.ok) {
          dispatch({ type: 'refused' });
          setRefusal(sent.refusal);
          // axios's 401 handling (toast, sign-in page) takes over from here
          if (sent.refusal === 'session') void getMiloMe().catch(() => undefined);
          return false;
        }
        let finished = false;
        for await (const event of readMiloStream(sent.stream)) {
          if (event.type === 'done') finished = true;
          dispatch({ type: 'event', event });
        }
        // the stream ended without milo's `done` (and the user did not stop it): the answer broke off
        if (!finished && !controller.signal.aborted) dispatch({ type: 'event', event: { type: 'error', message: t('fleet.milo.refusal.failed') } });
        dispatch({ type: 'done' });
        return true;
      } catch (error) {
        // stopped by the user, or the stream broke off: what streamed stays
        if ((error as Error).name !== 'AbortError') setRefusal('network');
        dispatch({ type: 'done' });
        return true;
      } finally {
        running.current = null;
        void queryClient.invalidateQueries({ queryKey: miloKeys.conversations() });
        void queryClient.invalidateQueries({ queryKey: miloKeys.me() });
      }
    },
    [state.conversationId, location.pathname, i18n.language, queryClient, t]
  );

  const stop = useCallback(() => running.current?.abort(), []);

  const reset = useCallback(() => {
    running.current?.abort();
    setRefusal(null);
    dispatch({ type: 'reset' });
  }, []);

  const open = useCallback(async (id: string) => {
    running.current?.abort();
    setRefusal(null);
    try {
      const conversation = await getConversation(id);
      dispatch({ type: 'load', conversationId: conversation.id, messages: messagesOf(conversation.messages) });
    } catch {
      setRefusal('not_found');
    }
  }, []);

  /** A long chat goes on in a new one that starts from a summary of the old */
  const continueChat = useCallback(async () => {
    if (!state.conversationId || running.current) return;
    setRefusal(null);
    try {
      const next = await continueConversation(state.conversationId);
      await open(next.conversationId);
      void queryClient.invalidateQueries({ queryKey: miloKeys.conversations() });
    } catch {
      setRefusal('failed');
    }
  }, [state.conversationId, open, queryClient]);

  return { state, refusal, ask, stop, reset, open, continueChat };
}
